import { useEffect, useMemo, useState } from 'react';
import {
  Modal, Stack, Group, Button, SegmentedControl, TextInput, Switch, Textarea, Box, Center,
} from '@mantine/core';
import { DatePickerInput, TimeInput } from '@mantine/dates';
import { modals } from '@mantine/modals';
import {
  IconPlane, IconTrain, IconCar, IconBed, IconDeviceFloppy, IconTrash,
} from '@tabler/icons-react';
import { LocationSelect } from './LocationSelect.tsx';
import { splitDateTime, type Transport } from '../lib/itinerary.ts';
import { parseLocalDate } from '../lib/dates.ts';
import type { DraftLeg } from './ItineraryRow.tsx';

/**
 * Tabbed travel-leg editor that mirrors the legacy Angular `summary` form:
 *
 *   ┌── Plane | Train | Road | Accommodation ─────────────────────────┐
 *   │ (fields depend on the selected transport type)                   │
 *   └──────────────────────────────────────────────────────────────────┘
 *   [ SAVE ]   [ DELETE ]   [ CANCEL ]
 *
 *   - Plane / Train: From + To, dep+arr date+time, ref number, pickup,
 *     (train only) booked
 *   - Road: From + To, single date + approx arrival time, car type
 *   - Accommodation (stay): City, optional Room, From + To dates only
 */
const TRANSPORTS: Array<{ value: Transport; label: string; icon: React.ReactNode }> = [
  { value: 'plane', label: 'Plane', icon: <IconPlane size={18} /> },
  { value: 'train', label: 'Train', icon: <IconTrain size={18} /> },
  { value: 'road',  label: 'Road',  icon: <IconCar size={18} /> },
  { value: 'stay',  label: 'Accomodation', icon: <IconBed size={18} /> },
];

export interface TravelEditModalProps {
  /** null = closed; an object = open with that leg as initial state */
  leg: DraftLeg | null;
  onClose(): void;
  onSave(next: DraftLeg): Promise<void>;
  onDelete(): Promise<void>;
}

export function TravelEditModal({ leg, onClose, onSave, onDelete }: TravelEditModalProps) {
  // All hooks must run unconditionally on every render — otherwise React
  // raises error #310 ("rendered more/fewer hooks than during the previous
  // render") when `leg` flips from null → an object.
  const [draft, setDraft] = useState<DraftLeg | null>(leg);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(leg); }, [leg]);

  const transport = (draft?.transport as Transport) ?? 'plane';
  const isNew = draft?.id == null;
  const dep = splitDateTime(draft?.departureDate ?? null);
  const arr = splitDateTime(draft?.arrivalDate ?? null);

  // Required-field validation per transport.
  const errors = useMemo(() => {
    if (!draft) return [];
    const errs: string[] = [];
    if (transport === 'stay') {
      if (draft.departureFromId == null) errs.push('city');
      if (!draft.departureDate) errs.push('from-date');
      if (!draft.arrivalDate) errs.push('to-date');
    } else {
      if (draft.departureFromId == null) errs.push('from');
      if (draft.arrivalToId == null) errs.push('to');
      if (!draft.departureDate) errs.push('dep-date');
      if (!(draft.departureTime ?? dep.time)) errs.push('dep-time');
      if (transport !== 'road') {
        if (!draft.arrivalDate) errs.push('arr-date');
      }
      if (!(draft.arrivalTime ?? arr.time)) errs.push('arr-time');
    }
    return errs;
  }, [draft, dep.time, arr.time, transport]);

  // Closed state — render nothing AFTER all hooks have run.
  if (!draft) return null;

  const isInvalid = errors.length > 0;

  function setField<K extends keyof DraftLeg>(k: K, v: DraftLeg[K]) {
    setDraft(prev => (prev ? { ...prev, [k]: v } : prev));
  }

  function setTransport(t: Transport) {
    setDraft(prev => (prev ? { ...prev, transport: t } : prev));
  }

  function setDeparture(date: string | null, time: string | null) {
    setDraft(prev => prev ? ({
      ...prev,
      departureDate: mergeIso(date, time ?? prev.departureTime ?? dep.time),
      departureTime: time ?? prev.departureTime ?? dep.time,
    }) : prev);
  }
  function setArrival(date: string | null, time: string | null) {
    setDraft(prev => prev ? ({
      ...prev,
      arrivalDate: mergeIso(date, time ?? prev.arrivalTime ?? arr.time),
      arrivalTime: time ?? prev.arrivalTime ?? arr.time,
    }) : prev);
  }

  async function handleSave() {
    if (!draft || isInvalid) return;
    setSaving(true);
    try {
      await onSave(draft);
    } finally {
      setSaving(false);
    }
  }

  function handleDelete() {
    if (isNew) {
      onClose();
      return;
    }
    modals.openConfirmModal({
      title: 'Delete travel leg',
      children: <span>Remove this leg from the itinerary?</span>,
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => { void onDelete(); },
    });
  }

  return (
    <Modal opened onClose={onClose} title={null} centered size="lg" withCloseButton={false} padding={0}>
      <Stack gap={0}>
        {/* Tab bar */}
        <Box p="md" pb={0}>
          <SegmentedControl
            value={transport}
            onChange={v => setTransport(v as Transport)}
            data={TRANSPORTS.map(t => ({
              value: t.value,
              label: (
                <Center inline style={{ gap: 6 }}>
                  {t.icon}
                  <span>{t.label}</span>
                </Center>
              ),
            }))}
            fullWidth
          />
        </Box>

        <Box p="md">
          {transport === 'stay'
            ? <StayFields draft={draft} setField={setField} />
            : <TravelFields
                draft={draft}
                transport={transport}
                setField={setField}
                setDeparture={setDeparture}
                setArrival={setArrival}
              />}
        </Box>

        <Group justify="flex-start" px="md" pb="md" pt="xs" style={{ borderTop: '1px solid var(--mantine-color-default-border)' }}>
          <Button
            variant="subtle"
            color="blue"
            leftSection={<IconDeviceFloppy size={16} />}
            disabled={isInvalid}
            loading={saving}
            onClick={handleSave}
          >
            SAVE
          </Button>
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconTrash size={16} />}
            onClick={handleDelete}
          >
            DELETE
          </Button>
          <Button variant="subtle" color="gray" onClick={onClose}>
            CANCEL
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ─── Plane / Train / Road fields ─────────────────────────────────────

function TravelFields({
  draft, transport, setField, setDeparture, setArrival,
}: {
  draft: DraftLeg;
  transport: Transport;
  setField: <K extends keyof DraftLeg>(k: K, v: DraftLeg[K]) => void;
  setDeparture: (date: string | null, time: string | null) => void;
  setArrival: (date: string | null, time: string | null) => void;
}) {
  const dep = splitDateTime(draft.departureDate ?? null);
  const arr = splitDateTime(draft.arrivalDate ?? null);
  const refLabel = transport === 'plane' ? 'Flight Number' : transport === 'train' ? 'Train Number' : '';
  const isRoad = transport === 'road';

  return (
    <Stack gap="md">
      <Group grow align="flex-start">
        <LocationSelect
          label="Departure From *"
          value={draft.departureFromId}
          onChange={id => setField('departureFromId', id)}
        />
        <LocationSelect
          label="Arrival To *"
          value={draft.arrivalToId}
          onChange={id => setField('arrivalToId', id)}
        />
      </Group>

      {isRoad ? (
        <Group grow align="flex-end">
          <DatePickerInput
            label="Date *"
            value={parseLocalDate(dep.date)}
            onChange={d => {
              const iso = d ? toIsoDate(d as Date) : null;
              setDeparture(iso, draft.departureTime ?? dep.time);
              setArrival(iso, draft.arrivalTime ?? arr.time);
            }}
            valueFormat="DD/MM/YYYY"
          />
          <TimeInput
            label="Approx Arrival Time *"
            value={draft.arrivalTime ?? arr.time ?? ''}
            onChange={e => setArrival(arr.date ?? dep.date, e.currentTarget.value || null)}
          />
          <SegmentedControl
            data={['Taxi', 'Bus', 'Own']}
            value={draft.carType ?? ''}
            onChange={v => setField('carType', v)}
          />
        </Group>
      ) : (
        <>
          <Group grow align="flex-end">
            <DatePickerInput
              label="Date *"
              value={parseLocalDate(dep.date)}
              onChange={d => setDeparture(d ? toIsoDate(d as Date) : null, draft.departureTime ?? dep.time)}
              valueFormat="DD/MM/YYYY"
            />
            <TimeInput
              label="Time hh:mm *"
              value={draft.departureTime ?? dep.time ?? ''}
              onChange={e => setDeparture(dep.date, e.currentTarget.value || null)}
            />
            <DatePickerInput
              label="Date *"
              value={parseLocalDate(arr.date)}
              onChange={d => setArrival(d ? toIsoDate(d as Date) : null, draft.arrivalTime ?? arr.time)}
              valueFormat="DD/MM/YYYY"
            />
            <TimeInput
              label="Time hh:mm *"
              value={draft.arrivalTime ?? arr.time ?? ''}
              onChange={e => setArrival(arr.date, e.currentTarget.value || null)}
            />
          </Group>

          <Group grow align="flex-end">
            <TextInput
              label={refLabel}
              value={draft.transportReference ?? ''}
              onChange={e => setField('transportReference', e.currentTarget.value)}
            />
            <Switch
              label="Pickup Required"
              checked={Boolean(draft.pickupRequired)}
              onChange={e => setField('pickupRequired', e.currentTarget.checked)}
              mt={24}
            />
          </Group>

          {transport === 'train' && (
            <Switch
              label="Booked"
              checked={Boolean(draft.booked)}
              onChange={e => setField('booked', e.currentTarget.checked)}
            />
          )}
        </>
      )}

      <Textarea
        label="Comments"
        autosize
        minRows={2}
        value={draft.comment ?? ''}
        onChange={e => setField('comment', e.currentTarget.value)}
      />
    </Stack>
  );
}

// ─── Accommodation fields ────────────────────────────────────────────

function StayFields({
  draft, setField,
}: {
  draft: DraftLeg;
  setField: <K extends keyof DraftLeg>(k: K, v: DraftLeg[K]) => void;
}) {
  const dep = splitDateTime(draft.departureDate ?? null);
  const arr = splitDateTime(draft.arrivalDate ?? null);
  return (
    <Stack gap="md">
      <Group grow align="flex-end">
        <LocationSelect
          label="City *"
          value={draft.departureFromId}
          onChange={id => {
            setField('departureFromId', id);
            // Stay's arrivalTo always mirrors departureFrom (normaliseLegForSave does this too).
            setField('arrivalToId', id);
          }}
        />
        <TextInput
          label="Room"
          value={draft.room ?? ''}
          onChange={e => setField('room', e.currentTarget.value)}
        />
      </Group>

      <Group grow align="flex-end">
        <DatePickerInput
          label="From *"
          value={parseLocalDate(dep.date)}
          onChange={d => {
            const iso = d ? toIsoDate(d as Date) : null;
            setField('departureDate', iso ? `${iso}T00:00:00` : null);
            setField('departureTime', '00:00');
          }}
          valueFormat="DD/MM/YYYY"
        />
        <DatePickerInput
          label="To *"
          value={parseLocalDate(arr.date)}
          onChange={d => {
            const iso = d ? toIsoDate(d as Date) : null;
            setField('arrivalDate', iso ? `${iso}T00:00:00` : null);
            setField('arrivalTime', '00:00');
          }}
          valueFormat="DD/MM/YYYY"
        />
      </Group>

      <Textarea
        label="Comments"
        autosize
        minRows={2}
        value={draft.comment ?? ''}
        onChange={e => setField('comment', e.currentTarget.value)}
      />
    </Stack>
  );
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function mergeIso(date: string | null | undefined, time: string | null | undefined): string | null {
  if (!date) return null;
  const t = (time && /^\d{2}:\d{2}/.test(time)) ? time.slice(0, 5) : '00:00';
  return `${date}T${t}:00`;
}
